import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { FiChevronLeft, FiChevronRight, FiEdit2, FiTag, FiTrash2, FiShoppingBag, FiCheck, FiChevronDown, FiChevronUp, FiActivity, FiSearch, FiLayers } from 'react-icons/fi';
import { WORKFLOW_STAGES, PRODUCT_TYPES } from '../../data/constants';
import ChecklistItem from '../ShopSetup/ChecklistItem';

const PriorityBadge = ({ priority }) => {
  const colors = {
    Low: 'bg-gray-100 text-gray-600 border-gray-200',
    Medium: 'bg-orange-50 text-terracotta border-orange-200',
    High: 'bg-red-50 text-red-600 border-red-200'
  };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase font-bold tracking-wider ${colors[priority]}`}>
      {priority}
    </span>
  );
};

const ProductCard = ({ product, updateProduct, deleteProduct, moveProduct, shops, productionTasks, researchTasks }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('production');
  
  // Normalize types data (handle legacy string 'type' or new array 'types')
  const initialTypes = product.types ? [...product.types] : (product.type ? [product.type] : ['T-Shirt']);
  
  const [editForm, setEditForm] = useState({
    ...product,
    types: initialTypes,
    shopIds: product.shopIds || [],
    checklist: product.checklist || {}
  });

  const stageIndex = WORKFLOW_STAGES.indexOf(product.status);
  const canMoveLeft = stageIndex > 0;
  const canMoveRight = stageIndex < WORKFLOW_STAGES.length - 1;

  const handleSave = () => {
    updateProduct(product.id, editForm);
    setIsEditing(false);
  };

  const toggleType = (type) => {
    const currentTypes = editForm.types || [];
    const updatedTypes = currentTypes.includes(type) 
      ? currentTypes.filter(t => t !== type) 
      : [...currentTypes, type];
    
    if (updatedTypes.length > 0) {
      setEditForm({ ...editForm, types: updatedTypes });
    }
  };

  const toggleShop = (shopId) => {
    const currentShops = editForm.shopIds || [];
    const updatedShops = currentShops.includes(shopId) 
      ? currentShops.filter(id => id !== shopId) 
      : [...currentShops, shopId];
    setEditForm({ ...editForm, shopIds: updatedShops });
  };

  const toggleChecklistItem = (itemId) => {
    const currentProgress = product.checklist || {};
    const itemData = currentProgress[itemId] || { completed: false };
    const updatedChecklist = {
      ...currentProgress,
      [itemId]: { ...itemData, completed: !itemData.completed }
    };
    updateProduct(product.id, { checklist: updatedChecklist });
  };

  const updateChecklistItemDetails = (itemId, details) => {
    const currentProgress = product.checklist || {};
    const updatedChecklist = { ...currentProgress, [itemId]: details };
    updateProduct(product.id, { checklist: updatedChecklist });
  };

  const getProgress = (tasks) => {
    if (!tasks || tasks.length === 0) return 0;
    const completed = tasks.filter(item => {
      const d = product.checklist?.[item.id];
      return typeof d === 'object' ? d.completed : !!d;
    }).length;
    return Math.round((completed / tasks.length) * 100);
  };

  const prodProgress = getProgress(productionTasks);
  const resProgress = getProgress(researchTasks);

  // Helper to get types for display
  const displayTypes = product.types || (product.type ? [product.type] : []);

  if (isEditing) {
    return (
      <div className="bg-white p-4 rounded-xl border-2 border-sage shadow-md mb-3 animate-fade-in max-h-[600px] overflow-y-auto z-10 relative">
        <input 
          className="w-full text-sm font-bold border-b border-cream-dark mb-4 pb-1 focus:outline-none focus:border-sage bg-transparent"
          value={editForm.title}
          onChange={e => setEditForm({ ...editForm, title: e.target.value })}
          placeholder="Product Title"
        />
        <div className="space-y-5 mb-4">
          <div>
            <label className="block text-[10px] font-bold text-espresso-light/60 uppercase mb-2">Product Types (Multi-select)</label>
            <div className="grid grid-cols-2 gap-1.5">
              {PRODUCT_TYPES.map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleType(t)}
                  className={`flex items-center justify-between px-2 py-2 rounded-lg border text-[10px] transition-all ${
                    editForm.types?.includes(t) 
                      ? 'bg-sage/10 border-sage text-sage font-bold' 
                      : 'bg-white border-cream-dark text-espresso-light hover:border-sage/40'
                  }`}
                >
                  {t}
                  {editForm.types?.includes(t) && <SafeIcon icon={FiCheck} className="w-2.5 h-2.5" />}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-bold text-espresso-light/60 uppercase mb-1">Priority</label>
              <select 
                className="w-full text-xs border border-cream-dark rounded p-2 bg-transparent focus:outline-none"
                value={editForm.priority}
                onChange={e => setEditForm({ ...editForm, priority: e.target.value })}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-espresso-light/60 uppercase mb-1">Deadline</label>
              <input 
                type="date"
                className="w-full text-xs border border-cream-dark rounded p-2 bg-transparent focus:outline-none"
                value={editForm.targetDate}
                onChange={e => setEditForm({ ...editForm, targetDate: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-espresso-light/60 uppercase mb-2">Linked Shops</label>
            <div className="grid grid-cols-1 gap-2">
              {shops.map(shop => (
                <button
                  key={shop.id}
                  type="button"
                  onClick={() => toggleShop(shop.id)}
                  className={`flex items-center justify-between p-2 rounded-lg border text-left transition-all ${
                    editForm.shopIds?.includes(shop.id) 
                      ? 'bg-terracotta/5 border-terracotta/40 text-terracotta font-bold' 
                      : 'bg-white border-cream-dark text-espresso-light'
                  }`}
                >
                  <span className="text-[11px] truncate">{shop.name}</span>
                  {editForm.shopIds?.includes(shop.id) && <SafeIcon icon={FiCheck} className="w-3 h-3" />}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center pt-2 border-t border-cream-dark">
          <button onClick={() => deleteProduct(product.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors">
            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
          </button>
          <div className="flex gap-2">
            <button onClick={() => setIsEditing(false)} className="text-xs px-3 py-2 rounded-lg text-espresso-light hover:bg-cream-dark">Cancel</button>
            <button onClick={handleSave} className="text-xs px-4 py-2 rounded-lg bg-sage text-white font-bold hover:bg-sage/90 shadow-sm">Save</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border transition-all duration-300 mb-3 group hover:shadow-md ${isExpanded ? 'border-sage ring-1 ring-sage/20' : 'border-cream-dark shadow-sm'}`}>
      <div className="p-4">
        <div className="flex justify-between items-start mb-2">
          <span className="text-[10px] font-mono text-terracotta bg-cream px-1.5 py-0.5 rounded font-bold">{product.designNumber}</span>
          <div className="flex gap-1">
            <button 
              onClick={() => setIsExpanded(!isExpanded)} 
              className={`p-1 rounded transition-colors ${isExpanded ? 'text-sage bg-sage/10' : 'text-espresso-light/40 hover:text-espresso'}`}
            >
              <SafeIcon icon={isExpanded ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
            </button>
            <button onClick={() => setIsEditing(true)} className="p-1 text-espresso-light/40 hover:text-espresso transition-colors">
              <SafeIcon icon={FiEdit2} className="w-4 h-4" />
            </button>
          </div>
        </div>

        <h4 className="font-bold text-sm text-espresso mb-2 leading-tight">{product.title || 'Untitled Design'}</h4>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {displayTypes.map(t => (
            <span key={t} className="inline-flex items-center gap-1 text-[9px] bg-cream-dark/50 text-espresso-light px-2 py-0.5 rounded-full font-bold">
              <SafeIcon icon={FiTag} className="w-2.5 h-2.5" /> {t}
            </span>
          ))}
          <PriorityBadge priority={product.priority} />
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <div className="flex justify-between text-[8px] font-bold text-espresso-light/40 uppercase mb-1">
              <span>Research</span>
              <span>{resProgress}%</span>
            </div>
            <div className="h-1 w-full bg-cream rounded-full overflow-hidden">
              <div className="h-full bg-terracotta transition-all duration-500" style={{ width: `${resProgress}%` }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-[8px] font-bold text-espresso-light/40 uppercase mb-1">
              <span>Production</span>
              <span>{prodProgress}%</span>
            </div>
            <div className="h-1 w-full bg-cream rounded-full overflow-hidden">
              <div className="h-full bg-sage transition-all duration-500" style={{ width: `${prodProgress}%` }} />
            </div>
          </div>
        </div>

        {product.shopIds && product.shopIds.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3 pt-2 border-t border-cream-dark/30">
            {product.shopIds.map(id => {
              const shop = shops.find(s => s.id === id);
              if (!shop) return null;
              return (
                <span key={id} className="text-[9px] px-1.5 py-0.5 bg-espresso/5 text-espresso-light rounded border border-espresso/10 flex items-center gap-1">
                  <SafeIcon icon={FiShoppingBag} className="w-2.5 h-2.5" />
                  {shop.name}
                </span>
              );
            })}
          </div>
        )}

        <div className="flex justify-between items-center pt-2 border-t border-cream-dark/50">
          <button onClick={() => moveProduct(product.id, -1)} disabled={!canMoveLeft} className={`p-1 rounded ${canMoveLeft ? 'text-espresso hover:bg-cream-dark' : 'text-cream-dark cursor-not-allowed'}`}>
            <SafeIcon icon={FiChevronLeft} className="w-4 h-4" />
          </button>
          <span className="text-[10px] font-bold text-espresso-light/60 uppercase tracking-wider">{product.status}</span>
          <button onClick={() => moveProduct(product.id, 1)} disabled={!canMoveRight} className={`p-1 rounded ${canMoveRight ? 'text-espresso hover:bg-cream-dark' : 'text-cream-dark cursor-not-allowed'}`}>
            <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-cream-dark bg-cream/5 animate-fade-in">
          <div className="flex border-b border-cream-dark">
            <button 
              onClick={() => setActiveSubTab('research')}
              className={`flex-1 py-3 text-[10px] font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-colors ${activeSubTab === 'research' ? 'bg-white text-terracotta border-b-2 border-terracotta' : 'text-espresso-light/40 hover:bg-cream/10'}`}
            >
              <SafeIcon icon={FiSearch} /> Research
            </button>
            <button 
              onClick={() => setActiveSubTab('production')}
              className={`flex-1 py-3 text-[10px] font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-colors ${activeSubTab === 'production' ? 'bg-white text-sage border-b-2 border-sage' : 'text-espresso-light/40 hover:bg-cream/10'}`}
            >
              <SafeIcon icon={FiLayers} /> Production
            </button>
          </div>
          <div className="p-4 space-y-2 max-h-[300px] overflow-y-auto custom-scrollbar">
            {(activeSubTab === 'research' ? researchTasks : productionTasks).map(item => (
              <ChecklistItem 
                key={item.id}
                item={item}
                progressData={product.checklist?.[item.id]}
                onToggle={() => toggleChecklistItem(item.id)}
                onUpdateDetails={(details) => updateChecklistItemDetails(item.id, details)}
              />
            ))}
            {(activeSubTab === 'research' ? researchTasks : productionTasks).length === 0 && (
              <p className="text-[10px] text-espresso-light/40 italic text-center py-8">
                No tasks in master template.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCard;